<template>
  <portal v-if="show" append to="modal-onboarding">
    <div ref="modalOnboardingRef" class="mc-modal__onboarding">
      <div v-if="page !== 'result'" class="mc-modal__onboarding-header">
        <h5 v-if="page == 'main'" class="font-size-18">
          {{ $t('notification.onboarding.modalTitle') }}
        </h5>
        <div v-if="page == 'detail'" class="d-flex justify-content-between">
          <span class="back-btn" @click="backToMain">
            <i class="mc-fa icon-outline_arrow-left"></i> {{ $t('global.back') }}
          </span>
          <i class="icon-outline_close close-btn" @click="closeModal" />
        </div>
      </div>
      <div class="mc-modal__onboarding-content">
        <template v-if="page == 'main'">
          <b-row>
            <b-col
              v-for="item in items"
              :key="item.templateId"
              sm="12"
              md="6"
              class="mc-modal__onboarding-list"
              @click="selectCategory(item)"
            >
              <div class="mc-modal__onboarding-list-item" :class="{ active: item.selected }">
                <h6>{{ $i18n.locale == 'id' ? item.nameIdn : item.nameEng }}</h6>
                <span>{{ $i18n.locale == 'id' ? item.descriptionIdn : item.descriptionEng }}</span>
              </div>
            </b-col>
          </b-row>
        </template>
        <template v-if="page == 'detail'">
          <div class="mc-modal__onboarding-selected-title">
            <h6>
              {{ $i18n.locale == 'id' ? selectedCategory.nameIdn : selectedCategory.nameEng }}
            </h6>
            <span class="text-muted">{{
              $i18n.locale == 'id'
                ? selectedCategory.descriptionIdn
                : selectedCategory.descriptionEng
            }}</span>
          </div>
          <div class="mc-modal__onboarding-selected-list">
            <span>{{ $t('notification.onboarding.selectNotification') }}</span>
            <div v-if="allDefaultAlertsActive" class="mc-modal__onboarding-selected-list--empty">
              <figure>
                <img
                  :src="illustrationPath"
                  :title="$t('controlTower.detail.emptyNotification')"
                  alt="Empty Notification"
                />
              </figure>
              <h5>
                {{ $t('notification.onboarding.emptyState') }}
              </h5>
            </div>
            <template v-for="defaultAlert in selectedCategory.defaultAlertCodes">
              <div
                v-if="!defaultAlert.isAlreadyActive"
                :key="defaultAlert.alertCode"
                class="mc-modal__onboarding-selected-list-item"
              >
                <div class="d-flex gap-2">
                  <McCheckbox
                    :id="defaultAlert.alertCode.toString()"
                    v-model="selectedAlert[defaultAlert.alertCode]"
                    @change="(event) => handleSelect(event, defaultAlert.alertCode)"
                  />
                  <label :for="defaultAlert.alertCode.toString()">
                    {{
                      $i18n.locale == 'id'
                        ? defaultAlert.descriptionIdn
                        : defaultAlert.descriptionEng
                    }}
                  </label>
                </div>
                <div class="mc-modal__onboarding-accordion">
                  <span @click="toggleExpand(defaultAlert.alertCode)">
                    {{ $t('landMeasurement.field.description') }}
                    <i
                      :class="
                        expandItem[defaultAlert.alertCode]
                          ? 'mc-fa icon-outline_chevron-up'
                          : 'mc-fa icon-outline_chevron-down'
                      "
                    ></i>
                  </span>
                </div>
              </div>
              <div
                v-if="expandItem[defaultAlert.alertCode]"
                :key="defaultAlert.alertCode"
                class="mc-modal__onboarding-selected-list-desc"
              >
                {{
                  $i18n.locale == 'id'
                    ? defaultAlert.fullDescriptionIdn
                    : defaultAlert.fullDescriptionEng
                }}
              </div>
            </template>
            <div
              v-if="hasActiveDefaultAlerts || hasActiveAdditionalAlerts"
              class="mc-modal__onboarding-selected-card"
            >
              <h6 class="mb-2">
                {{ $t('notification.onboarding.alreadyActive') }}
              </h6>
              <template v-for="alert in activeAlerts">
                <div
                  v-if="alert.isAlreadyActive"
                  :key="alert.alertCode"
                  class="mc-modal__onboarding-selected-list-item"
                >
                  <div class="d-flex gap-2">
                    <McCheckbox
                      :id="alert.alertCode.toString()"
                      v-model="alreadyActive[alert.alertCode]"
                      disabled
                    />
                    <span>
                      {{ $i18n.locale == 'id' ? alert.descriptionIdn : alert.descriptionEng }}
                    </span>
                  </div>
                  <div :id="`show-alert-rule-${alert.alertCode}`" class="mc-modal__onboarding-show">
                    <i class="mc-fa icon-outline_eye"></i>
                    <b-tooltip :target="`show-alert-rule-${alert.alertCode}`" triggers="hover">
                      {{ alert.alertRule.map((item) => item.label).join(', ') }}
                    </b-tooltip>
                  </div>
                </div>
              </template>
            </div>
            <McButton
              v-if="!allAdditionalAlertsActive && selectedCategory.additionalAlertCodes?.length > 0"
              variant="info"
              :text="
                showAdditional
                  ? $t('global.action.close')
                  : $t('navbar.dropdown.notification.button')
              "
              :icon="showAdditional ? 'icon-outline_chevron-up' : 'icon-outline_chevron-down'"
              iconPosition="right"
              size="sm"
              class="my-2"
              block
              @click="showAdditional = !showAdditional"
            />
            <template v-if="showAdditional">
              <div
                v-for="additional in selectedCategory.additionalAlertCodes"
                v-show="!additional.isAlreadyActive"
                :key="additional.alertCode"
              >
                <div class="mc-modal__onboarding-selected-list-item">
                  <div class="d-flex gap-2">
                    <McCheckbox
                      :id="additional.alertCode.toString()"
                      v-model="selectedAlert[additional.alertCode]"
                      @change="(event) => handleSelect(event, additional.alertCode)"
                    />
                    <label :for="additional.alertCode.toString()">
                      {{
                        $i18n.locale == 'id' ? additional.descriptionIdn : additional.descriptionEng
                      }}
                    </label>
                  </div>
                  <div class="mc-modal__onboarding-accordion">
                    <span @click="toggleExpand(additional.alertCode)">
                      {{ $t('landMeasurement.field.description') }}
                      <i
                        :class="
                          expandItem[additional.alertCode]
                            ? 'mc-fa icon-outline_chevron-up'
                            : 'mc-fa icon-outline_chevron-down'
                        "
                      ></i>
                    </span>
                  </div>
                </div>
                <div
                  v-if="expandItem[additional.alertCode]"
                  class="mc-modal__onboarding-selected-list-desc"
                >
                  {{
                    $i18n.locale == 'id'
                      ? additional.fullDescriptionIdn
                      : additional.fullDescriptionEng
                  }}
                </div>
              </div>
            </template>
          </div>
        </template>
        <template v-if="page == 'result'">
          <div class="mc-modal__onboarding-result">
            <img :src="require('@/assets/images/onboarding/bell-check.jpg')" alt="Success Icon" />
            <h6>
              {{
                hasAccess(['e4a-alert-center-main:read'])
                  ? $t('notification.onboarding.notificationActivated')
                  : $t('notification.onboarding.notificationRequested')
              }}
            </h6>
            <span class="text-muted">
              {{
                hasAccess(['e4a-alert-center-main:read'])
                  ? $t('notification.onboarding.setOtherNotification')
                  : $t('notification.onboarding.requestInProgress')
              }}
            </span>
          </div>
        </template>
      </div>
      <div class="mc-modal__onboarding-footer">
        <template v-if="page == 'main'">
          <McButton variant="secondary" :text="$t('global.cancel')" @click="closeModal" />
          <McButton :text="$t('global.save')" :disabled="!hasSelectedItem" @click="handleSave" />
        </template>
        <template v-if="page == 'detail'">
          <template
            v-if="allDefaultAlertsActive && (allAdditionalAlertsActive || !hasAdditionalAlerts)"
          >
            <div></div>
            <McButton
              variant="secondary"
              :text="$t('notification.onboarding.seeOther')"
              @click="backToMain"
            />
          </template>

          <template v-else>
            <div class="font-size-12">
              <span class="d-block me-2">{{ $t('notification.onboarding.autoApplied') }}</span>
              <a
                href="https://kb.mceasy.com/docs"
                class="text-primary text-decoration-underline"
                target="_blank"
              >
                {{ $t('maintenance.tooltip.learnMore') }}
              </a>
            </div>
            <McButton :text="$t('global.alert.text.confirm')" @click="handleConfirm" />
          </template>
        </template>
        <template v-if="page == 'result'">
          <McButton
            v-if="hasAccess(['e4a-alert-center-main:read'])"
            class="me-2"
            variant="secondary"
            :text="$t('notification.onboarding.setupOther')"
            block
            @click="openNotificationSetting"
          />
          <McButton :text="$t('global.finish')" block @click="closeModal" />
        </template>
      </div>
    </div>
  </portal>
</template>
<script>
import McButton from '@/components/McButton'
import McCheckbox from '@/components/McCheckbox'
import { UPDATE_COMPANY_PROFILE } from '@/services/api/setting/company/profile'
import {
  CREATE_BULK_NOTIFICATION_RULES,
  GET_NOTIFICATION_TEMPLATES,
} from '@/services/api/setting/notification/List'
import { Helper } from '@/mixin/helper'
import { MixPanel } from '@/mixin/mixpanel'
import { mapGetters } from 'vuex'

export default {
  components: {
    McButton,
    McCheckbox,
  },
  mixins: [Helper, MixPanel],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      page: 'main',
      items: [],
      selectedCategory: {},
      expandItem: {},
      showAdditional: false,
      selectedAlert: {},
      alreadyActive: {},
      selectedTemplate: [],
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    hasSelectedItem() {
      return this.items.some((item) => item.selected === true)
    },
    hasActiveDefaultAlerts() {
      return (
        this.selectedCategory.defaultAlertCodes &&
        this.selectedCategory.defaultAlertCodes.some((alert) => alert.isAlreadyActive === true)
      )
    },
    hasActiveAdditionalAlerts() {
      return (
        this.selectedCategory.additionalAlertCodes &&
        this.selectedCategory.additionalAlertCodes.some((alert) => alert.isAlreadyActive === true)
      )
    },
    activeAlerts() {
      const activeDefaultAlerts = this.selectedCategory.defaultAlertCodes
        ? this.selectedCategory.defaultAlertCodes.filter((alert) => alert.isAlreadyActive)
        : []

      const activeAdditionalAlerts = this.selectedCategory.additionalAlertCodes
        ? this.selectedCategory.additionalAlertCodes.filter((alert) => alert.isAlreadyActive)
        : []

      return [...activeDefaultAlerts, ...activeAdditionalAlerts]
    },
    allDefaultAlertsActive() {
      if (
        !this.selectedCategory.defaultAlertCodes ||
        this.selectedCategory.defaultAlertCodes.length === 0
      ) {
        return false
      }

      return this.selectedCategory.defaultAlertCodes.every(
        (alert) => alert.isAlreadyActive === true
      )
    },
    allAdditionalAlertsActive() {
      if (
        !this.selectedCategory.additionalAlertCodes ||
        this.selectedCategory.additionalAlertCodes.length === 0
      ) {
        return false
      }

      return this.selectedCategory.additionalAlertCodes.every(
        (alert) => alert.isAlreadyActive === true
      )
    },
    hasSelectedAlerts() {
      return Object.values(this.selectedAlert).some((value) => value === true)
    },
    illustrationPath() {
      return require('@/assets/images/illustration/whitelabel/success.png')
    },
    hasAdditionalAlerts() {
      return (
        this.selectedCategory.additionalAlertCodes &&
        this.selectedCategory.additionalAlertCodes.length > 0
      )
    },
  },
  mounted() {
    document.addEventListener('mousedown', this.handleClickOutside)
    this.getData()
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.handleClickOutside)
  },
  methods: {
    toggleExpand(alertCode) {
      this.$set(this.expandItem, alertCode, !this.expandItem[alertCode])
    },
    async getData() {
      const res = await this.$apollo.query({
        query: GET_NOTIFICATION_TEMPLATES,
        variables: {
          filter: {
            category: 'Recommendation',
            pagination: {
              page: 1,
              show: 999,
            },
          },
        },
        fetchPolicy: 'no-cache',
      })

      if (!res.data.alertRuleTemplates.isSuccessful) return
      this.items = res.data.alertRuleTemplates.alertRuleTemplates.data.map((item) => ({
        ...item,
        selected: false,
      }))
    },
    closeModal() {
      this.$emit('update:show', false)
      this.selectedAlert = {}
      this.backToMain()
    },
    handleClickOutside(event) {
      const modal = this.$refs.modalOnboardingRef
      if (modal && !modal.contains(event.target)) {
        this.closeModal()
      }
    },
    backToMain() {
      this.page = 'main'
      this.selectedCategory = {}
      this.alreadyActive = {}
      this.expandItem = {}
    },
    selectCategory(item) {
      this.page = 'detail'
      this.selectedCategory = item

      if (item.defaultAlertCodes && item.defaultAlertCodes.length > 0) {
        item.defaultAlertCodes.forEach((alert) => {
          if (alert.isAlreadyActive) {
            this.$set(this.alreadyActive, alert.alertCode, alert.isAlreadyActive)
          } else {
            this.$set(this.selectedAlert, alert.alertCode, true)
          }
          alert.fullDescriptionIdn =
            alert.fullDescriptionIdn == null || alert.fullDescriptionIdn == ''
              ? '-'
              : alert.fullDescriptionIdn
          alert.fullDescriptionEng =
            alert.fullDescriptionEng == null || alert.fullDescriptionEng == ''
              ? '-'
              : alert.fullDescriptionEng
        })
      }

      if (item.additionalAlertCodes && item.additionalAlertCodes.length > 0) {
        item.additionalAlertCodes.forEach((alert) => {
          if (alert.isAlreadyActive) {
            this.$set(this.alreadyActive, alert.alertCode, alert.isAlreadyActive)
          }
          alert.fullDescriptionIdn =
            alert.fullDescriptionIdn == null || alert.fullDescriptionIdn == ''
              ? '-'
              : alert.fullDescriptionIdn
          alert.fullDescriptionEng =
            alert.fullDescriptionEng == null || alert.fullDescriptionEng == ''
              ? '-'
              : alert.fullDescriptionEng
        })
      }
    },
    handleSelect(state, alertCode) {
      this.selectedAlert[alertCode] = state
    },
    handleConfirm() {
      const alertObjects = []
      if (
        this.selectedCategory.defaultAlertCodes &&
        this.selectedCategory.defaultAlertCodes.length > 0
      ) {
        this.selectedCategory.defaultAlertCodes.forEach((alert) => {
          if (this.selectedAlert[alert.alertCode] === true) {
            alertObjects.push(alert)
          }
        })
      }
      if (
        this.selectedCategory.additionalAlertCodes &&
        this.selectedCategory.additionalAlertCodes.length > 0
      ) {
        this.selectedCategory.additionalAlertCodes.forEach((alert) => {
          if (this.selectedAlert[alert.alertCode] === true) {
            alertObjects.push(alert)
          }
        })
      }
      if (alertObjects.length > 0) {
        const input = {
          templateId: this.selectedCategory.templateId,
          alertCodes: [...new Set(alertObjects.map((item) => item.alertCode))],
        }
        const existingTemplateIndex = this.selectedTemplate.findIndex(
          (template) => template.templateId === input.templateId
        )
        if (existingTemplateIndex !== -1) {
          this.$set(this.selectedTemplate, existingTemplateIndex, input)
        } else {
          this.selectedTemplate.push(input)
        }
      }

      //change status card
      const index = this.items.findIndex(
        (item) => item.templateId == this.selectedCategory.templateId
      )
      if (index !== -1) {
        this.items[index].selected = alertObjects.length > 0
      }
      this.backToMain()
    },
    async handleSave() {
      const res = await this.$apollo.mutate({
        mutation: CREATE_BULK_NOTIFICATION_RULES,
        variables: {
          input: {
            templates: this.selectedTemplate,
          },
        },
      })
      if (!res.data.createAlertRules.isSuccessful) {
        this.$mcToast.error(this.$t('notification.onboarding.failedSave'))
        return
      }
      this.track('Click Terapkan on Notification Onboarding (Web)', { userType: 'Existing User' })
      this.selectedTemplate = []
      this.selectedAlert = {}
      this.alreadyActive = {}
      this.page = 'result'
      const update = await this.$apollo.mutate({
        mutation: UPDATE_COMPANY_PROFILE,
        variables: {
          input: {
            id: this.getCompanyId(),
            setupNotifCompleted: true,
          },
        },
      })
      if (!update.data.updateCompany.isSuccessful) {
        this.$mcToast.error(update.data.updateCompany.message)
        return
      }
      this.$store.dispatch('identity/init').catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error refetching user info:', error)
      })
    },
    openNotificationSetting() {
      this.$router.push({ name: 'Setting Notification List' })
    },
  },
}
</script>
<style lang="scss" scoped>
.mc-modal__onboarding {
  position: fixed;
  top: 70px;
  left: 50%;
  transform: translateX(-50%);
  min-width: 300px;
  max-width: 480px;
  background: white;
  border: 1px solid #ccc;
  border-radius: 8px;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.2);
  z-index: 150;
  padding: 16px;
}
.mc-modal__onboarding-header {
  margin-bottom: 12px;
}
.mc-modal__onboarding-content {
  margin-bottom: 12px;
  max-height: 400px;
  overflow: hidden;
  overflow-y: auto;
  .row {
    margin: 0 -2px;
  }
}
.mc-modal__onboarding-list {
  padding: 4px;
}
.mc-modal__onboarding-list-item {
  padding: 0.75rem 1rem;
  border: 1px solid $teal-100;
  border-radius: 0.5rem;
  cursor: pointer;
  &:hover {
    background-color: $mep-secondary-100;
  }
  &.active {
    position: relative;
    background-color: $green-100;
    overflow: hidden;
    &:before {
      content: '';
      border-width: 40px 0 0 40px;
      border-style: solid;
      border-color: $success transparent;
      position: absolute;
      right: 0;
      top: 0;
    }
    &:after {
      content: '✓';
      color: $white;
      position: absolute;
      right: 8px;
      top: 4px;
    }
  }
}
.mc-modal__onboarding-selected-title {
  padding: 0.75rem 1rem;
  border: 1px solid $teal-100;
  border-radius: 0.5rem;
}
.mc-modal__onboarding-selected-list {
  margin: 0.5rem 0rem;
}
.mc-modal__onboarding-selected-card {
  border: 1px solid $teal-100;
  border-radius: 0.5rem;
  background-color: $teal-100;
  padding: 1rem;
  margin: 0.5rem 0rem;
}
.mc-modal__onboarding-selected-list-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.25rem 0rem;
}
.mc-modal__onboarding-selected-list--empty {
  text-align: center;
  padding: 20px;
  margin-bottom: 16px;
  img {
    width: 120px;
  }
}
.mc-modal__onboarding-accordion {
  position: relative;
  span {
    font-size: 11px;
    color: $mep-primary;
    cursor: pointer;
    .mc-fa {
      padding: 4px;
      margin-right: 20px;
      vertical-align: middle;
      &:before {
        font-size: 16px;
        color: $mep-primary;
      }
    }
  }
}
.mc-modal__onboarding-show {
  cursor: pointer;
  .mc-fa {
    font-size: 16px;
  }
}
.mc-modal__onboarding-selected-list-desc {
  margin-left: 20px;
  font-size: 13px;
  color: $gray-600;
}
.mc-modal__onboarding-result {
  padding: 0.75rem 1rem;
  text-align: center;
  img {
    margin-top: 16px;
    margin-bottom: 16px;
    height: 80px;
    width: 80px;
  }
  h6 {
    font-size: 16px;
  }
  span {
    font-size: 13px;
    color: $mep-color-text-300;
  }
}
.mc-modal__onboarding-footer {
  display: flex;
  justify-content: space-between;
}
.back-btn {
  cursor: pointer;
  padding-bottom: 8px;
  font-size: 12px;
  color: $mep-primary;
  .mc-fa {
    vertical-align: middle;
    font-size: 16px;
    &:before {
      color: $mep-primary;
    }
  }
}
.close-btn {
  position: absolute;
  width: 30px;
  right: 0px;
  margin: 0 auto;
  font-size: 16px;
  cursor: pointer;
  &:before {
    color: $mep-primary;
  }
}
</style>
